import { supabase } from "@/integrations/supabase/client";

type NotificationType = 'like' | 'comment' | 'save' | 'follow' | 'mention';

export const useCreateNotification = () => {
  const createNotification = async (
    userId: string,
    content: string,
    type: NotificationType,
    relatedId?: string
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          content,
          type,
          related_id: relatedId || null,
          is_read: false
        });

      if (error) {
        console.error('Error creating notification:', error);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error creating notification:', error);
      return false;
    }
  };

  const checkUserNotificationPreference = async (
    userId: string,
    preferenceType: 'notify_on_likes' | 'notify_on_comments' | 'notify_on_saves'
  ): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(preferenceType)
        .eq('id', userId)
        .single();

      if (error || !data) return true; // Default to true if can't fetch
      return data[preferenceType] !== false; // Default to true if null
    } catch {
      return true;
    }
  };

  const getPromptOwner = async (promptId: string): Promise<{ userId: string; title: string | null } | null> => {
    try {
      const { data, error } = await supabase
        .from('prompts')
        .select('user_id, title')
        .eq('id', promptId)
        .single();

      if (error || !data) return null;
      return { userId: data.user_id, title: data.title };
    } catch {
      return null;
    }
  };

  const notifyOnLike = async (
    promptId: string,
    likerUserId: string,
    likerUsername: string
  ) => {
    const promptOwner = await getPromptOwner(promptId);
    if (!promptOwner || promptOwner.userId === likerUserId) return;

    const shouldNotify = await checkUserNotificationPreference(promptOwner.userId, 'notify_on_likes');
    if (!shouldNotify) return;

    const promptTitle = promptOwner.title ? `"${promptOwner.title.slice(0, 30)}${promptOwner.title.length > 30 ? '...' : ''}"` : 'your prompt';
    await createNotification(
      promptOwner.userId,
      `@${likerUsername} liked ${promptTitle}`,
      'like',
      promptId
    );
  };

  const notifyOnSave = async (
    promptId: string,
    saverUserId: string,
    saverUsername: string
  ) => {
    const promptOwner = await getPromptOwner(promptId);
    if (!promptOwner || promptOwner.userId === saverUserId) return;

    const shouldNotify = await checkUserNotificationPreference(promptOwner.userId, 'notify_on_saves');
    if (!shouldNotify) return;

    const promptTitle = promptOwner.title ? `"${promptOwner.title.slice(0, 30)}${promptOwner.title.length > 30 ? '...' : ''}"` : 'your prompt';
    await createNotification(
      promptOwner.userId,
      `@${saverUsername} saved ${promptTitle}`,
      'save',
      promptId
    );
  };

  const notifyOnComment = async (
    promptId: string,
    commenterUserId: string,
    commenterUsername: string
  ) => {
    const promptOwner = await getPromptOwner(promptId);
    if (!promptOwner || promptOwner.userId === commenterUserId) return;

    const shouldNotify = await checkUserNotificationPreference(promptOwner.userId, 'notify_on_comments');
    if (!shouldNotify) return;

    const promptTitle = promptOwner.title ? `"${promptOwner.title.slice(0, 30)}${promptOwner.title.length > 30 ? '...' : ''}"` : 'your prompt';
    await createNotification(
      promptOwner.userId,
      `@${commenterUsername} commented on ${promptTitle}`,
      'comment',
      promptId
    );
  };

  return {
    createNotification,
    notifyOnLike,
    notifyOnSave,
    notifyOnComment,
    checkUserNotificationPreference,
    getPromptOwner
  };
};
